import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, Divider, Paper,
    Stack, TextField, Typography
} from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import EditNoteIcon from '@mui/icons-material/EditNote';
import axiosInstance from '../services/axiosInstance';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '—';

export default function SignContractPage() {
    const { token } = useParams();

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);

    // PIN step
    const [pin, setPin] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(null);

    // After PIN verified
    const [signingUrl, setSigningUrl] = useState(null);
    const [verifiedPin, setVerifiedPin] = useState(null); // kept for request-changes auth

    // Request changes step
    const [showChanges, setShowChanges] = useState(false);
    const [comment, setComment] = useState('');
    const [submittingChanges, setSubmittingChanges] = useState(false);
    const [changesDone, setChangesDone] = useState(false);

    useEffect(() => {
        axiosInstance.get(`/api/public/sign-contract/${token}`)
            .then(r => setInfo(r.data))
            .catch(() => setError('Dieser Link ist ungültig oder abgelaufen.'))
            .finally(() => setLoading(false));
    }, [token]);

    const handleVerify = async () => {
        setVerifying(true);
        setVerifyError(null);
        try {
            const r = await axiosInstance.post(`/api/public/sign-contract/${token}/verify`, { pin });
            setSigningUrl(r.data.signingUrl);
            setVerifiedPin(pin);
        } catch {
            setVerifyError('Ungültiger PIN. Bitte versuchen Sie es erneut.');
        } finally {
            setVerifying(false);
        }
    };

    const handleRequestChanges = async () => {
        if (!comment.trim()) return;
        setSubmittingChanges(true);
        try {
            await axiosInstance.post(`/api/public/sign-contract/${token}/request-changes`, {
                pin: verifiedPin,
                comment: comment.trim(),
            });
            setChangesDone(true);
        } catch {
            setVerifyError('Fehler beim Senden. Bitte versuchen Sie es erneut.');
        } finally {
            setSubmittingChanges(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#f0f4f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
        }}>
            <Paper elevation={3} sx={{ maxWidth: 500, width: '100%', p: 4, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#427898' }}>
                    Vertrag unterzeichnen
                </Typography>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                {!loading && !error && info && (
                    <>
                        <Stack spacing={1} sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">Vertrag</Typography>
                            <Typography variant="body1" fontWeight={600}>{info.contractDisplayName}</Typography>
                            {info.title && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Titel</Typography>
                                    <Typography variant="body1">{info.title}</Typography>
                                </>
                            )}
                            <Typography variant="body2" color="text.secondary">Kunde</Typography>
                            <Typography variant="body1">{info.customerName}</Typography>
                            <Typography variant="body2" color="text.secondary">Link gültig bis</Typography>
                            <Typography variant="body1">{fmtDate(info.expiresAt)}</Typography>
                        </Stack>

                        {info.status === 'SIGNED' && (
                            <Alert severity="success">Dieser Vertrag wurde bereits unterzeichnet.</Alert>
                        )}

                        {info.status === 'CANCELLED' && (
                            <Alert severity="warning">Dieser Unterzeichnungslink wurde storniert.</Alert>
                        )}

                        {info.status === 'DECLINED' && (
                            <Alert severity="error">Dieser Unterzeichnungslink wurde abgelehnt.</Alert>
                        )}

                        {/* PIN entry */}
                        {info.status === 'PENDING' && !signingUrl && (
                            <>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Bitte geben Sie den PIN ein, den Sie per E-Mail erhalten haben.
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="PIN"
                                        value={pin}
                                        onChange={e => setPin(e.target.value)}
                                        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                                        variant="outlined"
                                        fullWidth
                                        onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }}
                                    />
                                    {verifyError && <Alert severity="error">{verifyError}</Alert>}
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: '#427898', '&:hover': { bgcolor: '#355f77' } }}
                                        onClick={handleVerify}
                                        disabled={verifying || pin.length < 4}
                                        fullWidth
                                    >
                                        {verifying ? <CircularProgress size={20} color="inherit" /> : 'PIN bestätigen'}
                                    </Button>
                                </Stack>
                            </>
                        )}

                        {/* After PIN verified — show Sign or Request Changes */}
                        {signingUrl && !changesDone && (
                            <>
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    PIN bestätigt. Bitte wählen Sie eine Option:
                                </Alert>

                                {!showChanges ? (
                                    <Stack spacing={2}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            startIcon={<DrawIcon />}
                                            sx={{ bgcolor: '#427898', '&:hover': { bgcolor: '#355f77' } }}
                                            href={signingUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Vertrag unterzeichnen
                                        </Button>

                                        <Divider>oder</Divider>

                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<EditNoteIcon />}
                                            onClick={() => setShowChanges(true)}
                                        >
                                            Änderungen anfragen
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Stack spacing={2}>
                                        <Typography variant="body2">
                                            Beschreiben Sie die gewünschten Änderungen. Der Anbieter wird den Vertrag überarbeiten und Ihnen erneut zusenden.
                                        </Typography>
                                        <TextField
                                            autoFocus
                                            multiline
                                            minRows={4}
                                            fullWidth
                                            label="Ihr Kommentar"
                                            value={comment}
                                            onChange={e => setComment(e.target.value)}
                                            variant="outlined"
                                        />
                                        {verifyError && <Alert severity="error">{verifyError}</Alert>}
                                        <Stack direction="row" spacing={1}>
                                            <Button fullWidth variant="outlined" onClick={() => setShowChanges(false)}>
                                                Zurück
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onClick={handleRequestChanges}
                                                disabled={submittingChanges || !comment.trim()}
                                                sx={{ bgcolor: '#E07A5F', '&:hover': { bgcolor: '#c0624a' } }}
                                            >
                                                {submittingChanges
                                                    ? <CircularProgress size={20} color="inherit" />
                                                    : 'Änderungen anfragen'}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                )}
                            </>
                        )}

                        {changesDone && (
                            <Alert severity="info">
                                Ihre Änderungsanfrage wurde übermittelt. Der Anbieter wird sich bei Ihnen melden.
                            </Alert>
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
}
